import * as React from 'react'
import { formatPercentDisplay } from '../helpers/zoom-percent'
import type { ZoomMenuOption } from '../types/zoom-menu-option'

type UseZoomDraftArgs = {
  zoomPercent: number
  minPercent: number
  maxPercent: number
  menuOptions: ZoomMenuOption[]
}

/** The input's text: follows the applied zoom until the user types or previews a preset. */
export function useZoomDraft({
  zoomPercent,
  minPercent,
  maxPercent,
  menuOptions,
}: UseZoomDraftArgs) {
  const [draft, setDraft] = React.useState(() =>
    formatPercentDisplay(zoomPercent, minPercent, maxPercent)
  )
  /** Uncommitted draft in progress (typed input, or a hover preview not yet applied) — dims the
   * digits so it's visually distinct from the last actually-applied zoom value. Cleared (digits
   * turn `text-foreground`) on commit: Enter, blur-commit, or picking a menu option. */
  const [isDirty, setIsDirty] = React.useState(false)
  /** When true, blur should commit the draft (user edited the input). List highlight/hover preview must not flip this. */
  const dirtyFromInputChangeRef = React.useRef(false)

  const resetDraftFromProps = React.useCallback(() => {
    setDraft(formatPercentDisplay(zoomPercent, minPercent, maxPercent))
    setIsDirty(false)
    dirtyFromInputChangeRef.current = false
  }, [zoomPercent, minPercent, maxPercent])

  React.useEffect(() => {
    if (!isDirty) {
      setDraft(formatPercentDisplay(zoomPercent, minPercent, maxPercent))
    }
  }, [zoomPercent, minPercent, maxPercent, isDirty])

  /** Highlighting a preset previews its value in the input; any other row restores the applied zoom. */
  const applyHighlightPreview = React.useCallback(
    (index: number) => {
      const opt = menuOptions[index]

      if (opt?.kind === 'preset') {
        setIsDirty(true)
        dirtyFromInputChangeRef.current = false
        setDraft(formatPercentDisplay(opt.percent, minPercent, maxPercent))
        return
      }

      resetDraftFromProps()
    },
    [menuOptions, minPercent, maxPercent, resetDraftFromProps]
  )

  return {
    draft,
    setDraft,
    isDirty,
    setIsDirty,
    dirtyFromInputChangeRef,
    resetDraftFromProps,
    applyHighlightPreview,
  }
}
